/**
 * Auth0 SSO provider for ERIO ↔ EKSELENS shared-tenant universal login.
 *
 * Activated only when REACT_APP_AUTH0_DOMAIN and REACT_APP_AUTH0_CLIENT_ID
 * are present in .env. When absent, this component is a transparent
 * pass-through — the existing local-JWT login surface continues to work
 * unchanged.
 *
 * After successful Auth0 login the access token is captured by an axios
 * request interceptor (see authInterceptor.js) so every API call carries
 * `Authorization: Bearer <token>` automatically.
 */
import { useEffect } from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { setAuth0TokenGetter, setAuth0Logout } from "@/lib/authInterceptor";

const DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN || "";
const CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID || "";
const AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE || "https://api.erio.ekselens.com";
// Auth redirect URI MUST use window.location.origin directly (no env-var
// fallback) so it adapts to whichever domain the SPA is actually loaded
// from — preview, production, custom-domain, all work without re-config.
const REDIRECT_URI = `${window.location.origin}/auth/callback`;

export function isAuth0Configured() {
  return Boolean(DOMAIN && CLIENT_ID);
}

function TokenInjector() {
  const { getAccessTokenSilently, isAuthenticated, logout } = useAuth0();
  useEffect(() => {
    // Register the Auth0 logout fn so AuthContext.logout can terminate the
    // Auth0 session (otherwise a subsequent SSO click silently re-auths).
    setAuth0Logout(() =>
      logout({ logoutParams: { returnTo: window.location.origin + "/login" } })
    );
    if (!isAuthenticated) {
      setAuth0TokenGetter(null);
      return;
    }
    setAuth0TokenGetter(async () => {
      try {
        return await getAccessTokenSilently({
          authorizationParams: { audience: AUDIENCE },
        });
      } catch (e) {
        return null;
      }
    });
    return () => setAuth0TokenGetter(null);
  }, [getAccessTokenSilently, isAuthenticated, logout]);
  return null;
}

export function ErioAuth0Provider({ children }) {
  if (!isAuth0Configured()) {
    // Not configured — transparent passthrough so existing demo + local
    // auth continues to work in preview.
    return children;
  }
  return (
    <Auth0Provider
      domain={DOMAIN}
      clientId={CLIENT_ID}
      authorizationParams={{
        redirect_uri: REDIRECT_URI,
        audience: AUDIENCE,
        scope: "openid profile email",
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <TokenInjector />
      {children}
    </Auth0Provider>
  );
}

export { useAuth0 };
